import Link from 'next/link';
import { Layout, CtaForm, DownloadBrochureForm, WatchVideoDemo, AccordionMenu, FontAwesomeIcon } from '../../components';
import { INDUSTRIES } from '../../data/industries';
import { ASSET_BASE_URL, CALENDLY_LINK } from '../../constants';
import { omitKeyObject } from '../../utils';

export default function IndustryPage({ industry }) {
	const {
		slug,
		heading: { title, subtitle, description, badge, socialProof },
		problemSection,
		capabilities,
		benefits,
		spotlight,
		whyChoose,
		faqs,
		plan,
		footer,
	} = industry;

	const faqItems = faqs?.map((faq, index) => ({
		key: (index + 1).toString(),
		label: faq.question,
		children: (
			<div className="answer-content">
				<p className="answer-content--body">{faq.answer}</p>
			</div>
		),
	}));

	const faqJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqs?.map((faq) => ({
			'@type': 'Question',
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: faq.answer,
			},
		})),
	};

	return (
		<Layout pageName={title} jsonLd={faqJsonLd}>
			<div className={`industry-page industry-page--${slug}`}>
				{/* Hero Section */}
				<section className="industry-hero">
					<div className="container">
						<div className="industry-hero__wrapper">
							<div className="industry-hero__badge">
								<span className="industry-hero__badge-dot"></span>
								{badge || 'Industry Solutions'}
							</div>

							<h1 className="industry-hero__title">{title}</h1>
							<h2 className="industry-hero__subtitle">{subtitle}</h2>
							<p className="industry-hero__desc">{description}</p>

							<div className="industry-hero__actions">
								<a
									href={CALENDLY_LINK}
									target="_blank"
									rel="noreferrer"
									className="btn btn--primary industry-hero__btn"
								>
									Schedule a Demo
								</a>
								<DownloadBrochureForm classNameBtn="btn btn--secondary industry-hero__btn" />
								<WatchVideoDemo classNameBtn="btn btn--outline industry-hero__btn" />
							</div>

							{socialProof && (
								<div className="industry-hero__social-proof">
									<div className="industry-hero__award-badge">
										<FontAwesomeIcon name="fas fa-trophy" />
									</div>
									<span>{socialProof}</span>
								</div>
							)}
						</div>
					</div>
				</section>

				{/* Problem / Why Section */}
				{problemSection && (
					<section className="industry-problem">
						<div className="container">
							<div className="industry-problem__wrapper">
								<div className="industry-problem__content">
									<h2 className="industry-section-title">{problemSection.title}</h2>
									<p className="industry-section-desc">{problemSection.description}</p>

									{problemSection.keyPoints && (
										<ul className="industry-problem__points">
											{problemSection.keyPoints.map((point, idx) => (
												<li key={idx} className="industry-problem__point-item">
													<span className="industry-problem__point-icon">
														<FontAwesomeIcon name="fas fa-check-circle" />
													</span>
													<span>{point}</span>
												</li>
											))}
										</ul>
									)}
								</div>

								<div className="industry-problem__visual">
									<div className="industry-visual-card">
										<img
											src={`${ASSET_BASE_URL}/images/building-types/commercial_${problemSection.image || 1}.webp`}
											alt={problemSection.title}
											className="industry-visual-card__img"
										/>
									</div>
								</div>
							</div>
						</div>
					</section>
				)}

				{/* Capabilities Section */}
				{capabilities && capabilities.length > 0 && (
					<section className="industry-capabilities">
						<div className="container">
							<div className="industry-capabilities__header">
								<span className="industry-badge-pill">Core Capabilities</span>
								<h2 className="industry-section-title">What FacilityBot Does for Your Operations</h2>
								<p className="industry-section-desc">
									Everything you need to automate workflows, ensure regulatory compliance, and eliminate equipment
									downtime.
								</p>
							</div>

							<div className="industry-capabilities__grid">
								{capabilities.map((cap, idx) => (
									<div key={idx} className="industry-capability-card">
										<div className="industry-capability-card__header">
											<div className="industry-capability-card__icon-box">
												<FontAwesomeIcon name={`fas fa-${cap.icon || 'cogs'}`} />
											</div>
											<h3 className="industry-capability-card__title">{cap.title}</h3>
										</div>
										<p className="industry-capability-card__desc">{cap.description}</p>
										{cap.image && (
											<div className="industry-capability-card__diagram">
												<img
													src={`${ASSET_BASE_URL}/images/building-types/commercial_${cap.image}.webp`}
													alt={cap.title}
													loading="lazy"
												/>
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					</section>
				)}

				{/* Benefits Section */}
				{benefits && benefits.length > 0 && (
					<section className="industry-benefits">
						<div className="container">
							<div className="industry-benefits__header">
								<span className="industry-badge-pill industry-badge-pill--accent">Measurable Outcomes</span>
								<h2 className="industry-section-title">The Benefits Teams See with FacilityBot</h2>
							</div>

							<div className="industry-benefits__grid">
								{benefits.map((benefit, idx) => (
									<div key={idx} className="industry-benefit-card">
										<div className="industry-benefit-card__number">0{idx + 1}</div>
										<h3 className="industry-benefit-card__title">{benefit.title}</h3>
										<p className="industry-benefit-card__desc">{benefit.description}</p>
									</div>
								))}
							</div>
						</div>
					</section>
				)}

				{/* Specialized Sector Spotlight */}
				{spotlight && (
					<section className="industry-spotlight">
						<div className="container">
							<div className="industry-spotlight__wrapper">
								<div className="industry-spotlight__visual">
									<div className="industry-visual-card">
										<img
											src={`${ASSET_BASE_URL}/images/building-types/commercial_${spotlight.image || 1}.webp`}
											alt={spotlight.title}
											className="industry-visual-card__img"
										/>
									</div>
								</div>

								<div className="industry-spotlight__content">
									<span className="industry-badge-pill">{spotlight.subtitle || 'Specialized Focus'}</span>
									<h2 className="industry-section-title">{spotlight.title}</h2>
									<p className="industry-section-desc">{spotlight.description}</p>

									{spotlight.points && (
										<ul className="industry-spotlight__points">
											{spotlight.points.map((pt, idx) => (
												<li key={idx} className="industry-spotlight__point-item">
													<span className="industry-spotlight__point-bullet">
														<FontAwesomeIcon name="fas fa-check" />
													</span>
													<span>{pt}</span>
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						</div>
					</section>
				)}

				{/* Why Choose Section */}
				{whyChoose && (
					<section className="industry-why-choose">
						<div className="container">
							<div className="industry-why-choose__box">
								<h2 className="industry-why-choose__title">{whyChoose.title}</h2>
								<p className="industry-why-choose__desc">{whyChoose.description}</p>
								<div className="industry-why-choose__stats">
									<div className="industry-stat-item">
										<div className="industry-stat-item__value">10M+</div>
										<div className="industry-stat-item__label">Sq. Ft. Managed</div>
									</div>
									<div className="industry-stat-item">
										<div className="industry-stat-item__value">50%</div>
										<div className="industry-stat-item__label">Faster Response Times</div>
									</div>
									<div className="industry-stat-item">
										<div className="industry-stat-item__value">100%</div>
										<div className="industry-stat-item__label">Audit-Ready Compliance</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				)}

				{/* Pricing Plans Link Banner */}
				<div className="pricing-wrapper">
					<div className="container">
						<h2 className="pricing-wrapper__title">{plan}</h2>

						<button className="btn btn--plans">
							<Link href="/pricing-plans">view plans and pricing</Link>
						</button>
					</div>
				</div>

				{/* Frequently Asked Questions */}
				{faqs && faqs.length > 0 && (
					<section className="industry-faq">
						<div className="container">
							<div className="industry-faq__wrapper">
								<div className="industry-faq__header">
									<span className="industry-badge-pill">Got Questions?</span>
									<h2 className="industry-section-title">Frequently Asked Questions</h2>
								</div>
								<div className="industry-faq__accordion">
									<AccordionMenu items={faqItems} />
								</div>
							</div>
						</div>
					</section>
				)}

				{/* Bottom CTA Form */}
				<CtaForm title={footer} />
			</div>
		</Layout>
	);
}

export async function getServerSideProps({ params }) {
	try {
		const industry = INDUSTRIES.map((item) => omitKeyObject('submenu', item)).find(
			({ slug }) => slug === params.slug,
		);

		if (!industry) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				industry,
			},
		};
	} catch (err) {
		console.error('getServerSideProps error in industries/[slug].js:', err);
		return {
			notFound: true,
		};
	}
}
